import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import PageContentHeader from '../components/layout/PageContentHeader'

const PageHeaderContext = createContext(null)

export const PageHeaderProvider = ({ children }) => {
  const [header, setHeaderState] = useState(null)

  const setPageHeader = useCallback((config) => {
    setHeaderState(config ?? null)
  }, [])

  const clearPageHeader = useCallback(() => {
    setHeaderState(null)
  }, [])

  const value = useMemo(
    () => ({ header, setPageHeader, clearPageHeader }),
    [header, setPageHeader, clearPageHeader]
  )

  return (
    <PageHeaderContext.Provider value={value}>
      {children}
    </PageHeaderContext.Provider>
  )
}

export const PageHeaderSlot = () => {
  const ctx = useContext(PageHeaderContext)
  const navigate = useNavigate()

  if (!ctx?.header) {
    return null
  }

  const { header } = ctx
  const handleBack =
    header.onBack ??
    (header.backTo ? () => navigate(header.backTo) : () => navigate(-1))

  return (
    <PageContentHeader
      title={header.title}
      subtitle={header.subtitle}
      breadcrumbs={header.breadcrumbs}
      titleIcon={header.titleIcon}
      actions={header.actions}
      showBack={header.showBack !== false}
      onBack={handleBack}
    />
  )
}

/**
 * Register a fixed page header (back + title) in the main content area.
 * Cleared automatically when the page unmounts.
 */
export const usePageHeader = ({
  title,
  subtitle,
  breadcrumbs,
  backTo,
  onBack,
  showBack = true,
  titleIcon,
  actions,
} = {}) => {
  const ctx = useContext(PageHeaderContext)

  if (!ctx) {
    throw new Error('usePageHeader must be used within PageHeaderProvider')
  }

  const { setPageHeader, clearPageHeader } = ctx

  React.useEffect(() => {
    if (!title) {
      clearPageHeader()
      return undefined
    }

    setPageHeader({
      title,
      subtitle,
      breadcrumbs,
      backTo,
      onBack,
      showBack,
      titleIcon,
      actions,
    })

    return () => clearPageHeader()
  }, [
    title,
    subtitle,
    breadcrumbs,
    backTo,
    onBack,
    showBack,
    titleIcon,
    actions,
    setPageHeader,
    clearPageHeader,
  ])
}

export default PageHeaderContext
